import React from 'react'
import { FaSeedling, FaCogs, FaPaw, FaFish, FaArrowRight } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import ImageWithFallback from '../components/ImageWithFallback'
import { activities } from '../data/siteData'

const Activities = () => {
  const activityDetails = {
    'Production': {
      icon: FaSeedling,
      color: 'from-green-500 to-green-600',
      stats: ['500 hectares cultivés', '15 variétés', '3 saisons/an']
    },
    'Transformation': {
      icon: FaCogs,
      color: 'from-blue-500 to-blue-600',
      stats: ['2 unités de transformation', '50 tonnes/mois', 'Certification ISO']
    },
    'Élevage': {
      icon: FaPaw,
      color: 'from-amber-500 to-amber-600',
      stats: ['200 têtes de bétail', '5 fermes partenaires', 'Alimentation bio']
    },
    'Aquaculture': {
      icon: FaFish,
      color: 'from-cyan-500 to-cyan-600',
      stats: ['10 bassins', '5 tonnes/mois', 'Espèces locales']
    }
  }

  return (
    <div className="Activities">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Nos Activités
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-90">
              Découvrez nos quatre domaines d'expertise qui font la richesse 
              de notre coopérative agricole
            </p>
          </div>
        </div>
      </section>

      {/* Activities Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {activities.map((activity, index) => {
              const details = activityDetails[activity.title] || activityDetails['Production']
              const Icon = details.icon

              return (
                <div
                  key={activity.id}
                  className="card overflow-hidden group"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="relative">
                    <ImageWithFallback
                      src={activity.image}
                      alt={activity.title}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-r ${details.color} opacity-90`}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Icon className="text-6xl text-white drop-shadow-lg" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h2 className="text-3xl font-bold text-white mb-2 text-shadow">
                        {activity.title}
                      </h2>
                    </div>
                  </div>
                  
                  <div className="p-8">
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {activity.description}
                    </p>
                    
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Caractéristiques principales :
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {activity.features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Chiffres clés :
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {details.stats.map((stat, idx) => (
                          <div key={idx} className="text-center p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">{stat}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Link 
                      to="/contact"
                      className="btn-primary inline-flex items-center space-x-2 w-full justify-center"
                    >
                      <span>En savoir plus</span>
                      <FaArrowRight />
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre Processus
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              De la production à la commercialisation, découvrez notre approche intégrée
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { title: 'Planification', description: 'Analyse des besoins et planification des cultures selon les saisons' },
              { title: 'Production', description: 'Mise en œuvre des techniques agricoles durables et respectueuses' },
              { title: 'Transformation', description: 'Valorisation des produits dans nos unités de transformation' },
              { title: 'Commercialisation', description: 'Distribution vers les marchés locaux et régionaux' }
            ].map((step, index) => (
              <div
                key={index}
                className="text-center"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="w-16 h-16 bg-primary-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-500">
        <div className="container-custom">
          <div className="text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Intéressé par nos activités ?
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Contactez-nous pour découvrir comment participer à nos programmes 
              ou devenir partenaire de nos activités.
            </p>
            <Link 
              to="/contact" 
              className="btn-secondary text-lg px-8 py-4"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Activities 